import ArrowBackIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowFwdIcon from "@mui/icons-material/ArrowForwardRounded";
import { Alert, AlertTitle, Box, Button, Step, StepLabel, Stepper } from "@mui/material";
import { useReducer, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { api } from "../../../../../api/client";
import useClientContext from "../../../../../contexts/useClientContext";
import useClientPageTitle from "../../../../../contexts/useClientPageTitle";
import useImpersonationBlockContext from "../../../../../contexts/useImpersonationBlockContext";
import { pageRoutes } from "../../../../../routes";
import { workspaceInvestorSelector } from "../../../../../store/state/user/selectors";
import LeavePageConfirmationDialog from "../../common/LeavePageConfirmationDialog";
import SettingsWrapper from "../../SettingsWrapper";
import { isFundInvestorEligibleForBankSelfService } from "../fundAssignmentsHelper";
import { useBankSettingsNavigation } from "../navigation";
import FundAssignmentsForm from "./FundAssignmentsForm";
import NewBankAccountForm from "./NewBankAccountForm";
import NewBankAccountReview from "./NewBankAccountReview";
import { StateStep, getInitialState, reducer, stateToCreateInvestorBankAccountRequest } from "./newBankAccountState";

const createBankAccount = withErrorHandling(api.bankAccounts.createBankAccount);

const stepLabels: [StateStep, string][] = [
  [StateStep.BankAccount, "Bank Account"],
  [StateStep.FundAssignments, "Fund Assignments"],
  [StateStep.Review, "Review"],
];

const NewBankAccountPage = () => {
  useClientPageTitle("New Bank Account");

  const { clientCode } = useClientContext();
  const { newBankAccountPageNavigationState: navigationState, navigateToBankAccountsPage } =
    useBankSettingsNavigation();

  const { sendNotification, sendNotificationError } = useNotificationContext();
  const { isBlockedUnderImpersonation, showImpersonationBlockedDialog } = useImpersonationBlockContext();
  const workspaceInvestor = useSelector(workspaceInvestorSelector);

  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [state, dispatch] = useReducer(
    reducer,
    getInitialState(
      workspaceInvestor?.fundInvestors.filter(isFundInvestorEligibleForBankSelfService) ?? [],
      navigationState?.availableFundInvestorIds ?? [],
      workspaceInvestor?.investorTitle ?? ""
    )
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attachmentsBusy, setAttachmentsBusy] = useState(false);

  if (!navigationState || !workspaceInvestor) {
    return <Navigate to={`/${clientCode}/${pageRoutes.settings}/lp/bank`} />;
  }

  if (!state.fundAssignments.some((assignment) => !assignment.disabled)) {
    return (
      <Alert severity="warning">
        <AlertTitle>No funds available for assignments</AlertTitle>
        <Button variant="text" onClick={() => navigateToBankAccountsPage()}>
          Go Back
        </Button>
      </Alert>
    );
  }

  const handleBackButtonClick = () => {
    setShowCancelConfirmation(true);
  };

  const handleConfirmLeave = () => {
    setShowCancelConfirmation(false);
    navigateToBankAccountsPage();
  };

  const handleCancelLeave = () => {
    setShowCancelConfirmation(false);
  };

  const handlePreviousStep = () => {
    dispatch({ type: "previous_step" });
  };

  const handleNextStep = async () => {
    if (state.step !== StateStep.Review) {
      dispatch({ type: "next_step" });
      return;
    }

    setIsSubmitting(true);
    const payload = stateToCreateInvestorBankAccountRequest(state);
    const [, error] = await createBankAccount(workspaceInvestor.investorId, payload);
    setIsSubmitting(false);
    if (error) {
      if (isBlockedUnderImpersonation(error)) {
        showImpersonationBlockedDialog();
        return;
      }
      logError(error, "[NewBankAccountPage] createBankAccount");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("The request has been submitted");
    navigateToBankAccountsPage();
  };

  const nextStepLabel = stepLabels[state.step + 1]?.[1] ?? "Send Request";

  return (
    <SettingsWrapper
      title="New Bank Account"
      subtitle={workspaceInvestor.investorTitle}
      scrollOwner="wrapper"
      onBackButtonClick={handleBackButtonClick}
      Subheader={
        <Box sx={{ py: 1.5, px: 3, display: "flex", justifyContent: "center" }}>
          <Stepper activeStep={state.step}>
            {stepLabels.map(([index, label]) => (
              <Step key={index}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      }
      Footer={
        <Box sx={{ py: 2, px: 3, display: "flex", justifyContent: "space-between" }}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ArrowBackIcon />}
            onClick={handlePreviousStep}
            disabled={attachmentsBusy}
            sx={{ visibility: state.step === StateStep.BankAccount ? "hidden" : "visible" }}
          >
            Previous
          </Button>
          <Button
            loading={isSubmitting}
            variant="contained"
            endIcon={<ArrowFwdIcon />}
            onClick={handleNextStep}
            disabled={state.isNextStepDisabled || attachmentsBusy}
          >
            {nextStepLabel}
          </Button>
        </Box>
      }
    >
      {state.step === StateStep.BankAccount && <NewBankAccountForm state={state} dispatch={dispatch} />}
      {state.step === StateStep.FundAssignments && <FundAssignmentsForm state={state} dispatch={dispatch} />}
      {state.step === StateStep.Review && (
        <NewBankAccountReview
          investorId={workspaceInvestor.investorId}
          state={state}
          dispatch={dispatch}
          onAttachmentsBusyChange={setAttachmentsBusy}
          attachmentsDisabled={isSubmitting}
        />
      )}
      <LeavePageConfirmationDialog
        open={showCancelConfirmation}
        onConfirm={handleConfirmLeave}
        onCancel={handleCancelLeave}
      />
    </SettingsWrapper>
  );
};

export default NewBankAccountPage;
